import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import './LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <div className="legal-page">
        <div className="legal-container">
          <div className="legal-header">
            <h1>Privacy Policy</h1>
            <p className="legal-updated">Last Updated: April 9, 2026</p>
          </div>

          <div className="legal-content">
            <section className="legal-section">
              <h2>1. Introduction</h2>
              <p>
                iGamesTech, operated by iTwelveTech ("Company", "we", "us", or "our"), is committed to protecting 
                the privacy and personal data of its users ("User", "you", or "your"). This Privacy Policy 
                ("Policy") explains how we collect, use, store, process, disclose, and protect your personal information 
                in compliance with the Digital Personal Data Protection Act, 2023 ("DPDP Act") and other applicable 
                Indian data protection laws.
              </p>
              <p>
                By accessing or using our Platform, you acknowledge that you have read, understood, and consent to the 
                practices described in this Policy.
              </p>
              <p>
                For details about cookie categories and your cookie controls, please review our <Link to="/cookie-policy" className="legal-inline-link">Cookie Policy</Link>.
              </p>
            </section>

            <section className="legal-section">
              <h2>2. Data Fiduciary Status</h2>
              <p>
                iTwelveTech acts as a "Data Fiduciary" under the DPDP Act, 2023, meaning we determine the purpose 
                and means of processing your personal data. We are responsible for ensuring lawful, fair, and transparent 
                processing of your personal information.
              </p>
            </section>

            <section className="legal-section">
              <h2>3. Information We Collect</h2>
              
              <h3>3.1 Personal Information</h3>
              <ul>
                <li><strong>Identity Information:</strong> Name, username, email address, phone number, date of birth</li>
                <li><strong>Account Information:</strong> Login credentials, user preferences, profile information</li>
                <li><strong>Contact Information:</strong> Mailing address, billing address, email, phone</li>
                <li><strong>Payment Information:</strong> Processed securely through third-party gateways</li>
                <li><strong>Technical Information:</strong> IP address, device type, browser, usage data</li>
                <li><strong>Gameplay Data:</strong> Game progress, scores, achievements, purchases</li>
              </ul>

              <h3>3.2 Automatically Collected Information</h3>
              <p>
                We automatically collect technical information such as browser type, device details, IP address, language, 
                pages viewed, timestamps, and interaction events for security, diagnostics, and service improvement.
              </p>
            </section>

            <section className="legal-section">
              <h2>4. How We Use Your Information</h2>
              
              <h3>4.1 Service Provision</h3>
              <ul>
                <li>Create and manage user accounts</li>
                <li>Provide, maintain, and improve services</li>
                <li>Process payments and transactions</li>
                <li>Enable multiplayer features</li>
                <li>Personalize gaming experience</li>
              </ul>

              <h3>4.2 Communication</h3>
              <ul>
                <li>Respond to inquiries and support requests</li>
                <li>Send service-related updates</li>
                <li>Provide customer support</li>
                <li>Send promotional materials (with consent)</li>
              </ul>

              <h3>4.3 Platform Improvement</h3>
              <ul>
                <li>Analyze usage patterns</li>
                <li>Develop new features</li>
                <li>Conduct research and analytics</li>
                <li>Monitor platform performance</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>5. Legal Basis for Processing</h2>
              <p>We process your personal data based on:</p>
              <ul>
                <li><strong>Consent:</strong> Explicit consent for specific purposes</li>
                <li><strong>Contract Performance:</strong> Necessary for service delivery</li>
                <li><strong>Legal Obligation:</strong> Required by law</li>
                <li><strong>Legitimate Interests:</strong> For business purposes without overriding your rights</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>6. Data Sharing and Disclosure</h2>
              
              <h3>6.1 Service Providers</h3>
              <p>
                We engage third-party providers for payment processing, cloud hosting, analytics, and customer support. 
                These providers have access only to perform specific tasks and are obligated to protect your data.
              </p>

              <h3>6.2 Legal Requirements</h3>
              <p>
                We may disclose information when required by law, court order, or to protect our rights and safety.
              </p>

              <h3>6.3 With Your Consent</h3>
              <p>
                We may share information with third parties when you provide explicit consent.
              </p>
            </section>

            <section className="legal-section">
              <h2>7. Data Security</h2>
              <p>
                We implement reasonable security measures to protect your personal information including:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security audits</li>
                <li>Access controls and monitoring</li>
                <li>Employee training on data protection</li>
              </ul>
              <p>
                However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, 
                we cannot guarantee absolute security.
              </p>
            </section>

            <section className="legal-section">
              <h2>8. Data Retention</h2>
              <p>
                We retain personal information only as long as necessary for the purposes collected, comply with legal 
                obligations, and resolve disputes. Retention periods vary based on data type:
              </p>
              <ul>
                <li>Account information: While active and reasonable period thereafter</li>
                <li>Transaction records: As required by tax and accounting laws</li>
                <li>Communications: For customer service purposes</li>
                <li>Analytics data: May be aggregated and anonymized</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>9. Your Rights Under DPDP Act, 2023</h2>
              
              <h3>9.1 Right to Access</h3>
              <p>Request access to your personal data and information about processing.</p>

              <h3>9.2 Right to Correction</h3>
              <p>Request correction of inaccurate or incomplete data.</p>

              <h3>9.3 Right to Erasure</h3>
              <p>Request deletion of personal data, subject to legal requirements.</p>

              <h3>9.4 Right to Withdraw Consent</h3>
              <p>Withdraw consent at any time where processing is based on consent.</p>

              <h3>9.5 Right to Grievance Redressal</h3>
              <p>File complaints with our Grievance Officer or Data Protection Board of India.</p>

              <p className="legal-extra-top">
                To exercise these rights, contact us at <strong className="legal-highlight-email">info@igamestech.com</strong>
              </p>
            </section>

            <section className="legal-section">
              <h2>10. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar technologies for authentication, security, analytics, and preferences management. 
                Cookie-specific practices, categories, retention timelines, and opt-out instructions are maintained in our 
                dedicated <Link to="/cookie-policy" className="legal-inline-link">Cookie Policy</Link>.
              </p>
            </section>

            <section className="legal-section">
              <h2>11. Children's Privacy</h2>
              <p>
                Our Platform is not directed to individuals under 18. We do not knowingly collect information from 
                children. If you believe a child has provided information, contact us immediately.
              </p>
            </section>

            <section className="legal-section">
              <h2>12. International Data Transfers</h2>
              <p>
                Personal information may be transferred to countries with different data protection laws. We ensure 
                appropriate safeguards are in place to protect your information.
              </p>
            </section>

            <section className="legal-section">
              <h2>13. Third-Party Links</h2>
              <p>
                Our Platform may contain links to third-party services. We are not responsible for their privacy 
                practices. Review their policies before providing information.
              </p>
            </section>

            <section className="legal-section">
              <h2>14. Changes to This Policy</h2>
              <p>
                We may update this Policy to reflect changes in practices or legal requirements. Continued use after 
                updates constitutes acceptance of changes.
              </p>
            </section>

            <section className="legal-section">
              <h2>15. Contact Us</h2>
              <p>
                For questions, concerns, or requests regarding this Privacy Policy:
              </p>
              <div className="contact-info">
                <p><strong>Email:</strong> info@igamestech.com</p>
                <p><strong>Phone:</strong> +91 92113 43912</p>
                <p><strong>Address:</strong> Unit No. 1017, 10th Floor, SS Omnia, Sector 88, Gurugram</p>
              </div>
            </section>

            <section className="legal-section legal-footer-section">
              <p className="legal-company-info">
                <strong>Data Fiduciary Details:</strong><br />
                iTwelveTech (operating iGamesTech)<br />
                Unit No. 1017, 10th Floor, SS Omnia, Sector 88, Gurugram<br />
                Email: info@igamestech.com | Phone: +91 92113 43912<br /><br />
                This policy is compliant with the Digital Personal Data Protection Act, 2023 and the Information 
                Technology Act, 2000.
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
